import React, { FC, memo, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { NavHashLink } from 'react-router-hash-link';
import { Manager, Popper, Reference } from 'react-popper';
// @ts-ignore
import useEventOutside from '@omtanke/react-use-event-outside';
import Collapse from '../../components/bootstrap/Collapse';
import Icon from '../../components/icon/Icon';
import Tooltips from '../../components/bootstrap/Tooltips';
import { useChatUnread } from '../../contexts/chatUnreadContext';
import ThemeContext from '../../contexts/themeContext';
import useDarkMode from '../../hooks/useDarkMode';
import { TIcons } from '../../type/icons-type';
import List from './List';

interface IItemProps {
	children?: ReactNode;
	to?: string;
	title?: string;
	icon?: TIcons;
	id?: string;
	parentId?: string;
	rootId: string;
	isHorizontal?: boolean;
	notification?: boolean | string;
	isMore?: boolean;
	hide?: boolean;
	activeItem?: string;
	setActiveItem?(...args: unknown[]): unknown;
	/** Whether the item is locked (feature disabled) - shows lock icon\n\t * @userstory US-MENU-07, US-MENU-08, US-FG-02, US-FG-10 */
	locked?: boolean;
	/** When true, the item is non-clickable (no navigation). @userstory US-STRIPE-07, US-STRIPE-08 */
	disabled?: boolean;
	/** Tooltip text shown on hover when the item is disabled. */
	disabledTooltip?: string;
	/** When true, items with both path and submenu will navigate AND expand submenu */
	navigateOnClick?: boolean;
	/** CSS class for nav icon color-coding (e.g., 'nav-c-blue') */
	iconColorClass?: string;
}
const Item: FC<IItemProps> = ({
	children,
	to,
	title,
	icon,
	id,
	parentId,
	rootId,
	isHorizontal,
	notification,
	isMore,
	hide,
	locked = false,
	disabled = false,
	disabledTooltip,
	navigateOnClick,
	iconColorClass,
	...props
}) => {
	const { darkModeStatus } = useDarkMode();
	const { width } = useWindowSize();
	const { setAsideStatus, setLeftMenuStatus, setRightMenuStatus } = useContext(ThemeContext);
	const { unreadCount } = useChatUnread();
	const { t } = useTranslation('menu');

	const ACTIVE = props.activeItem === id;
	const chatNotificationCount =
		(id === 'company-chat' || id === 'talent-chat') && unreadCount > 0
			? unreadCount
			: undefined;
	const effectiveNotification = chatNotificationCount ?? notification;
	const effectiveNotificationLabel =
		typeof chatNotificationCount === 'number'
			? t('chatUnreadBadge', { count: chatNotificationCount })
			: undefined;

	const handleClick = () => {
		if (typeof props.setActiveItem !== 'undefined') {
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			ACTIVE ? props.setActiveItem(null) : props.setActiveItem(id);
		}
	};

	const linkHandleClick = () => {
		// For Mobile Design
		if (width < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)) setAsideStatus(false);
		setLeftMenuStatus(false);
		setRightMenuStatus(false);
	};

	const ANCHOR_LINK_PATTERN = /^#/i;
	const location = useLocation();

	// For aside menu
	const here = typeof to === 'string' && to !== '/' && location.pathname.includes(to);
	// For top menu
	const match = to !== '/' && location.pathname === to;

	const LINK_CLASS = classNames('navigation-link', 'navigation-link-pill', {
		collapsed: !!children && !isHorizontal,
		active: isHorizontal ? match : here,
		'navigation-link-locked': locked,
		'navigation-link-disabled': disabled,
	});

	const INNER = (
		<>
			<span className='navigation-link-info'>
				{icon && <Icon className='navigation-icon' icon={icon} />}
				{title && <span className='navigation-text'>{t(title) as ReactNode}</span>}
				{locked && (
					<Icon
						id={`${rootId}__${id}--lock`}
						icon='Lock'
						className='navigation-lock-icon ms-2 text-muted'
						style={{ fontSize: '14px', opacity: 0.6 }}
					/>
				)}
			</span>
			{(!!children || !!effectiveNotification) && (
				<span className='navigation-link-extra'>
					{typeof effectiveNotification === 'number' && effectiveNotification > 0 ? (
						<span
							className='badge rounded-pill bg-danger ms-2'
							data-testid={
								id === 'company-chat' || id === 'talent-chat'
									? 'navigation-chat-unread-badge'
									: undefined
							}
							aria-label={effectiveNotificationLabel}
							title={effectiveNotificationLabel}>
							{effectiveNotification > 99 ? '99+' : effectiveNotification}
						</span>
					) : (
						!!effectiveNotification && (
							<Icon
								icon='Circle'
								className={classNames(
									'navigation-notification',
									{
										[`text-${effectiveNotification}`]:
											typeof effectiveNotification === 'string',
										'text-danger': typeof effectiveNotification !== 'string',
									},
									'animate__animated animate__heartBeat animate__infinite animate__slower',
								)}
							/>
						)
					)}
					{!!children && <Icon className='navigation-arrow' icon='ChevronRight' />}
				</span>
			)}
		</>
	);

	// Disabled items are non-clickable with optional tooltip (US-STRIPE-07, US-STRIPE-08)
	// Locked items navigate to page (page handles feature gating)
	const WITHOUT_CHILD =
		!children &&
		!hide &&
		(disabled ? (
			disabledTooltip ? (
				<Tooltips title={disabledTooltip} placement='right'>
					<span
						id={`${rootId}__${id}--link`}
						className={LINK_CLASS}
						role='link'
						aria-disabled='true'>
						{INNER}
					</span>
				</Tooltips>
			) : (
				<span
					id={`${rootId}__${id}--link`}
					className={LINK_CLASS}
					role='link'
					aria-disabled='true'>
					{INNER}
				</span>
			)
		) : (
			(typeof to === 'string' && ANCHOR_LINK_PATTERN.test(to) && (
				<NavHashLink
					id={`${rootId}__${id}--link`}
					className={LINK_CLASS}
					to={to}
					onClick={linkHandleClick}>
					{INNER}
				</NavHashLink>
			)) || (
				<NavLink
					end
					id={`${rootId}__${id}--link`}
					// @ts-ignore
					className={classNames(LINK_CLASS, ({ isActive }) => (isActive ? 'active' : ''))}
					to={`../${to}`}
					onClick={linkHandleClick}>
					{INNER}
				</NavLink>
			)
		));

	// Dropdown
	const dropdownRef = useRef(null);

	const dropdownButtonRef = useRef(null);
	const setButtonRef = useCallback((node: null, ref: (arg0: any) => any) => {
		dropdownButtonRef.current = node;
		return ref(node);
	}, []);

	const dropdownListRef = useRef(null);
	const setListRef = useCallback((node: null, ref: (arg0: any) => any) => {
		dropdownListRef.current = node;
		return ref(node);
	}, []);

	const [dropdownStatus, setDropdownStatus] = useState(false);

	const dropdownButtonHandleClick = () => {
		setDropdownStatus(!dropdownStatus);
	};

	// Clicking outside to close
	const closeMenu = useCallback(() => {
		setDropdownStatus(false);
	}, []);
	useEventOutside(dropdownRef, 'mousedown', closeMenu);
	useEventOutside(dropdownRef, 'touchstart', closeMenu);

	if (children) {
		// submenu && in header
		if (isHorizontal) {
			return (
				<Manager>
					<li
						ref={dropdownRef}
						className={classNames('navigation-item', 'dropdown', {
							'navigation-item-more': isMore,
						})}>
						<Reference>
							{({ ref }) => (
								<span
									// @ts-ignore
									ref={(node) => setButtonRef(node, ref)}
									id={`${rootId}__${id}--link`}
									className={LINK_CLASS}
									// data-bs-toggle='dropdown'
									// data-bs-target={`#${rootId}__${id}`}
									aria-expanded={dropdownStatus}
									aria-controls={`${rootId}__${id}`}
									role='button'
									tabIndex={-1}
									onClick={dropdownButtonHandleClick}
									onKeyDown={dropdownButtonHandleClick}>
									{INNER}
								</span>
							)}
						</Reference>
						{dropdownStatus && (
							<Popper
								placement='bottom-start'
								modifiers={[
									{
										name: 'flip',
										options: {
											fallbackPlacements: [`bottom-end`, `bottom-start`],
										},
									},
								]}>
								{({ ref, style, placement }) => (
									<List
										// @ts-ignore
										ref={(node) => setListRef(node, ref)}
										style={style}
										data-placement={placement}
										id={`${rootId}__${id}`}
										className={classNames(
											'dropdown-menu',
											{
												'dropdown-menu-dark': darkModeStatus,
											},
											'show',
										)}
										ariaLabelledby={`${rootId}__${id}--link`}
										rootId={rootId}
										parentId={`${rootId}__${parentId}`}
										onMouseLeave={() => setDropdownStatus(false)}>
										{children}
									</List>
								)}
							</Popper>
						)}
					</li>
				</Manager>
			);
		}
		// submenu && in aside
		if (to && navigateOnClick) {
			// Has both path and submenu — navigate AND expand/toggle submenu
			return (
				<li className={classNames('navigation-item', iconColorClass)}>
					<NavLink
						id={`${rootId}__${id}--link`}
						className={LINK_CLASS}
						to={`../${to}`}
						onClick={() => {
							handleClick();
							linkHandleClick();
						}}>
						{INNER}
					</NavLink>
					<Collapse isOpen={ACTIVE} isChildClone>
						<List
							id={`${rootId}__${id}`}
							ariaLabelledby={`${rootId}__${id}--link`}
							rootId={rootId}
							parentId={`${rootId}__${parentId}`}
							onMouseLeave={closeMenu}>
							{children}
						</List>
					</Collapse>
				</li>
			);
		}
		// submenu && in aside && no path — just toggle collapse
		return (
			<li className={classNames('navigation-item', iconColorClass)}>
				<span
					id={`${rootId}__${id}--link`}
					className={LINK_CLASS}
					// data-bs-toggle='collapse'
					// data-bs-target={`#${rootId}__${id}`}
					aria-expanded={ACTIVE}
					aria-controls={`${rootId}__${id}`}
					role='button'
					tabIndex={-1}
					onClick={handleClick}
					onKeyDown={handleClick}>
					{INNER}
				</span>
				<Collapse isOpen={ACTIVE} isChildClone>
					<List
						id={`${rootId}__${id}`}
						ariaLabelledby={`${rootId}__${id}--link`}
						rootId={rootId}
						parentId={`${rootId}__${parentId}`}
						onMouseLeave={closeMenu}>
						{children}
					</List>
				</Collapse>
			</li>
		);
	}
	// without submenu
	return <li className={classNames('navigation-item', iconColorClass)}>{WITHOUT_CHILD}</li>;
};

export default memo(Item);
